/**
 * Conexão por QR code.
 *
 * ---------------------------------------------------------------------------
 * O POLLING, QUE É O PONTO DELICADO DESTA TELA
 * ---------------------------------------------------------------------------
 * O QR do WhatsApp vale por poucos segundos: uma imagem pedida uma vez só está
 * velha antes de a pessoa destravar o celular. Então a consulta se repete —
 * `QR_POLL_INTERVAL_MS` — e a imagem troca sozinha.
 *
 * Repetir é fácil; PARAR é o que costuma faltar. Este drawer para em quatro
 * situações, e nenhuma delas depende de o usuário fazer nada:
 *
 *  1. conectou (`ALREADY_CONNECTED`) — fecha sozinho e confirma;
 *  2. o drawer fechou — `enabled: false` derruba o intervalo junto (é o teste
 *     `polling-qr.test.tsx`, que existe porque aba esquecida consultando de 4
 *     em 4 segundos é requisição infinita no servidor de produção);
 *  3. o estado é um beco sem saída (`NOT_STARTED`, `ERROR`) — insistir não muda
 *     nada, e o usuário ganha um botão em vez de um spinner eterno;
 *  4. `QR_MAX_ATTEMPTS` tentativas — quem abriu e foi almoçar não deixa a
 *     consulta rodando para sempre.
 *
 * `PENDING` **não** para o polling e **não** é erro: é a sessão subindo.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Drawer, ErrorState, Skeleton, Spinner, useToast } from '@mgdlead/ui';
import { RefreshCw, Smartphone } from 'lucide-react';

import { FormAlert } from '@mgdlead/ui';
import {
  QR_INSTRUCTIONS,
  QR_STATE_MESSAGE,
  QR_STATE_TITLE,
} from '../../../features/monitor/monitor-ui';
import { useConnectMonitor, useMonitorQr } from '../../../lib/api';
import type { MonitorQrState } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';

/** De quanto em quanto tempo o QR é pedido de novo. Explícito, nunca implícito. */
export const QR_POLL_INTERVAL_MS = 4_000;

/** Teto de tentativas: 30 × 4s ≈ 2 minutos de janela aberta. */
export const QR_MAX_ATTEMPTS = 30;

/**
 * Quantas respostas `NOT_STARTED` toleramos antes de desistir.
 *
 * A sessão leva alguns segundos para nascer no provider depois do `connect`, e
 * nesse intervalo o WAHA responde "sessão não existe". Com tolerância zero, quem
 * clicava em "Criar e conectar" via "a conexão não está ativa" e o QR nunca vinha.
 * 5 respostas ≈ 20s, tempo de sobra para subir, e ainda curto o bastante para não
 * deixar ninguém olhando uma tela que não vai mudar.
 */
export const QR_NOT_STARTED_TOLERANCE = 5;

export interface QrDrawerProps {
  open: boolean;
  workspaceId: string;
  monitorId: string | null;
  monitorName: string;
  onOpenChange: (open: boolean) => void;
  /** Chamado uma vez, quando o provider confirma que o número entrou. */
  onConnected: () => void;
}

export function QrDrawer({
  open,
  workspaceId,
  monitorId,
  monitorName,
  onOpenChange,
  onConnected,
}: QrDrawerProps) {
  const { toast } = useToast();
  const connect = useConnectMonitor();

  /** Quantas respostas já chegaram nesta abertura. Zera a cada reabertura. */
  const [attempts, setAttempts] = useState(0);
  /**
   * Instante em que o drawer abriu. Resposta anterior a isso é lixo da abertura
   * passada — um QR já vencido — e não pode decidir se o polling continua.
   */
  const [openedAt, setOpenedAt] = useState(0);

  useEffect(() => {
    if (!open) return;
    setAttempts(0);
    setOpenedAt(Date.now());
  }, [open, monitorId]);

  const exhausted = attempts >= QR_MAX_ATTEMPTS;

  // Primeiro render da abertura: ainda não há resposta desta rodada.
  const [current, setCurrent] = useState<MonitorQrState | null>(null);

  /**
   * `NOT_STARTED` logo após abrir NÃO é beco sem saída: quem acabou de clicar em
   * "Criar e conectar" chega aqui com a sessão ainda nascendo no provider, e o
   * WAHA responde "sessão não existe" por alguns segundos. Tratar isso como
   * definitivo fazia a tela dizer "conexão não está ativa" e parar de tentar —
   * o QR nunca aparecia.
   *
   * Depois de algumas respostas seguidas, aí sim é real: a sessão não subiu.
   */
  const notStartedIsFinal =
    current?.state === 'NOT_STARTED' && attempts >= QR_NOT_STARTED_TOLERANCE;

  /**
   * `EXPIRED` é beco sem saída como os outros: a sessão morreu junto com o código, e
   * perguntar de novo devolve `EXPIRED` para sempre. Quem tira dali é o botão que
   * reinicia — e é o `retry()` dele que zera o contador e volta a consultar.
   */
  const deadEnd =
    current !== null &&
    (current.state === 'ALREADY_CONNECTED' ||
      current.state === 'ERROR' ||
      current.state === 'EXPIRED' ||
      notStartedIsFinal);

  const polling = open && monitorId !== null && !exhausted && !deadEnd;

  const qr = useMonitorQr(workspaceId, monitorId, {
    enabled: polling,
    refetchInterval: QR_POLL_INTERVAL_MS,
  });

  /**
   * Só o que chegou DEPOIS desta abertura conta. `dataUpdatedAt` é o carimbo do
   * próprio React Query, então não há relógio novo entrando na conta.
   */
  const dataUpdatedAt = qr.dataUpdatedAt;
  const data = qr.data;
  useEffect(() => {
    if (dataUpdatedAt === 0 || dataUpdatedAt < openedAt || data === undefined) return;
    setCurrent(data);
    setAttempts((previous) => previous + 1);
  }, [dataUpdatedAt, openedAt, data]);

  useEffect(() => {
    if (!open) setCurrent(null);
  }, [open]);

  /** Fechar e confirmar acontece UMA vez, mesmo que o efeito rode de novo. */
  const confirmed = useRef(false);
  useEffect(() => {
    if (!open) {
      confirmed.current = false;
      return;
    }
    if (current?.state !== 'ALREADY_CONNECTED' || confirmed.current) return;
    confirmed.current = true;
    toast({
      title: 'Monitorador conectado',
      // O servidor adota os grupos e varre os participantes sozinho (`resync.service.ts`):
      // dizer isso evita a pessoa ir reimportar o que já está lá.
      description: `${monitorName} está no ar. Os grupos dele estão sendo atualizados sozinhos — não precisa importar de novo.`,
      tone: 'success',
    });
    onConnected();
    onOpenChange(false);
  }, [current, monitorName, onConnected, onOpenChange, open, toast]);

  /** Recomeça a rodada: zera o contador e pede um código novo agora. */
  const retry = useCallback(() => {
    setCurrent(null);
    setAttempts(0);
    setOpenedAt(Date.now());
    void qr.refetch();
  }, [qr]);

  /** `NOT_STARTED` só sai do lugar reiniciando a sessão — esperar não resolve. */
  const reconnect = useCallback(() => {
    if (monitorId === null) return;
    connect.mutate(
      { workspaceId, monitorId },
      {
        onSuccess: () => retry(),
        onError: (error: unknown) =>
          toast({
            title: 'Não deu para reiniciar',
            description: humanizeError(error),
            tone: 'danger',
          }),
      },
    );
  }, [connect, monitorId, retry, toast, workspaceId]);

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      title={`Conectar ${monitorName}`}
      description="A janela se fecha sozinha assim que o WhatsApp confirmar a conexão."
      footer={
        <Button variant="secondary" onClick={() => onOpenChange(false)}>
          Fechar
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <QrBody
          state={current}
          loading={qr.isPending && current === null}
          requestError={qr.isError ? humanizeError(qr.error) : null}
          exhausted={exhausted}
          reconnecting={connect.isPending}
          onRetry={retry}
          onReconnect={reconnect}
        />

        <QrInstructions />
      </div>
    </Drawer>
  );
}

/* ========================================================================== */
/*  Corpo — os 5 estados do procedure, um por um                               */
/* ========================================================================== */

interface QrBodyProps {
  state: MonitorQrState | null;
  loading: boolean;
  /** Falha de REDE/permissão, diferente do estado `ERROR` que o servidor nomeia. */
  requestError: string | null;
  exhausted: boolean;
  reconnecting: boolean;
  onRetry: () => void;
  onReconnect: () => void;
}

function QrBody({
  state,
  loading,
  requestError,
  exhausted,
  reconnecting,
  onRetry,
  onReconnect,
}: QrBodyProps) {
  if (requestError !== null) {
    return (
      <ErrorState
        title="Não conseguimos pedir o código"
        description={requestError}
        onRetry={onRetry}
        retryLabel="Tentar de novo"
        compact
      />
    );
  }

  if (loading && state === null) {
    return (
      <div className="flex flex-col items-center gap-3 py-2">
        <Skeleton className="size-58 max-w-full rounded-md" />
        <p className="text-sm text-muted-foreground">{QR_STATE_MESSAGE.PENDING}</p>
      </div>
    );
  }

  if (exhausted && state?.state !== 'ALREADY_CONNECTED') {
    return (
      <div className="flex flex-col items-center gap-3 py-2 text-center">
        <p className="text-md font-medium">O código parou de ser atualizado</p>
        <p className="max-w-prose text-sm text-muted-foreground">
          Faz alguns minutos que esta janela está aberta, então paramos de gerar códigos novos para
          não ficar consultando à toa. Gere um novo quando estiver com o celular na mão.
        </p>
        <Button
          variant="primary"
          leadingIcon={<RefreshCw />}
          onClick={onRetry}
          className="max-md:w-full"
        >
          Gerar um código novo
        </Button>
      </div>
    );
  }

  if (state === null) return null;

  switch (state.state) {
    case 'QR':
      return (
        <div className="flex flex-col items-center gap-3">
          <p className="text-md font-medium">{QR_STATE_TITLE.QR}</p>
          {/*
            Fundo branco fixo: o PNG do QR vem com transparência e, no tema
            escuro, um código preto sobre fundo escuro simplesmente não é lido
            pela câmera. É o raro caso em que a cor não pode sair do token.
          */}
          <img
            src={`data:${state.mimeType};base64,${state.base64}`}
            alt="QR code para conectar o WhatsApp"
            width={232}
            height={232}
            className="size-58 max-w-full rounded-md bg-white p-2"
          />
          <p className="max-w-prose text-center text-sm text-muted-foreground">
            {QR_STATE_MESSAGE.QR}
          </p>
        </div>
      );

    case 'PENDING':
      /* Não é erro: é a sessão subindo. Por isso spinner e texto calmo. */
      return (
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <Spinner label="Preparando a conexão" />
          <p className="text-md font-medium">{QR_STATE_TITLE.PENDING}</p>
          <p className="max-w-prose text-sm text-muted-foreground">{QR_STATE_MESSAGE.PENDING}</p>
        </div>
      );

    case 'ALREADY_CONNECTED':
      return (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p className="text-md font-medium text-success">{QR_STATE_TITLE.ALREADY_CONNECTED}</p>
          <p className="max-w-prose text-sm text-muted-foreground">
            {QR_STATE_MESSAGE.ALREADY_CONNECTED}
          </p>
        </div>
      );

    case 'NOT_STARTED':
      return (
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <p className="text-md font-medium">{QR_STATE_TITLE.NOT_STARTED}</p>
          <p className="max-w-prose text-sm text-muted-foreground">
            {QR_STATE_MESSAGE.NOT_STARTED}
          </p>
          <Button
            variant="primary"
            leadingIcon={<RefreshCw />}
            loading={reconnecting}
            loadingLabel="Reiniciando a conexão"
            onClick={onReconnect}
            className="max-md:w-full"
          >
            Conectar de novo
          </Button>
        </div>
      );

    case 'EXPIRED':
      return (
        <div className="flex flex-col items-center gap-3 py-2 text-center">
          <p className="text-md font-medium">{QR_STATE_TITLE.EXPIRED}</p>
          <p className="max-w-prose text-sm text-muted-foreground">{QR_STATE_MESSAGE.EXPIRED}</p>
          {/*
            `onReconnect`, e não `onRetry`: a sessão morreu junto com o código, e refazer a
            consulta devolveria o mesmo estado para sempre. O que tira daqui é reiniciar.
          */}
          <Button
            variant="primary"
            leadingIcon={<RefreshCw />}
            loading={reconnecting}
            loadingLabel="Gerando outro código"
            onClick={onReconnect}
            className="max-md:w-full"
          >
            Gerar outro código
          </Button>
        </div>
      );

    case 'ERROR':
      return (
        <div className="flex flex-col gap-3">
          <p className="text-md font-medium">{QR_STATE_TITLE.ERROR}</p>
          <FormAlert>{state.message}</FormAlert>
          <p className="text-sm text-muted-foreground">{QR_STATE_MESSAGE.ERROR}</p>
          <Button
            variant="secondary"
            leadingIcon={<RefreshCw />}
            onClick={onRetry}
            className="max-md:w-full"
          >
            Tentar de novo
          </Button>
        </div>
      );
  }
}

/* ========================================================================== */
/*  Instruções                                                                 */
/* ========================================================================== */

/**
 * Quem opera tráfego não necessariamente já conectou um aparelho ao WhatsApp.
 * Sem este passo a passo o QR é um quadrado sem sentido na tela.
 */
function QrInstructions() {
  return (
    <div className="border-t border-border pt-3">
      <p className="mb-2 flex items-center gap-2 text-sm font-medium">
        <Smartphone aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
        Como ler o código no celular
      </p>
      <ol className="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-muted-foreground">
        {QR_INSTRUCTIONS.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="mt-2 text-2xs text-muted-foreground">
        No iPhone o caminho é Configurações e depois Aparelhos conectados; no Android, o menu de
        três pontos e depois Aparelhos conectados.
      </p>
    </div>
  );
}
